import { Link } from "react-router-dom";
import type { Integration } from "../../../types/integration";
import { Logo } from "../Logo";
import { StatusPill } from "../StatusPill";
import styles from "./IntegrationCard.module.css";

type IntegrationCardProps = {
  integration: Integration;
  featured?: boolean;
};

const renderPill = (integration: Integration): JSX.Element | null => {
  // IP-206: a broken connection is the most urgent thing to show, so it wins
  // over the lifecycle/included pills. Only ever set once a connect flow has run
  // (flag on); prod stays unaffected with no connection rows.
  if (
    integration.connectionStatus === "error" ||
    integration.connectionStatus === "revoked"
  ) {
    return <StatusPill variant="error">Needs attention</StatusPill>;
  }
  // Connected integrations are surfaced in the hero "Active integrations"
  // panel now, not as a badge here, so the teaser card no longer renders a
  // "Connected" pill (the catalogue keeps its own so users don't double-add).
  // The lifecycle/included pills below remain for admin-driven states.
  if (integration.status === "active") {
    return <StatusPill variant="active">Active</StatusPill>;
  }
  if (integration.status === "provisioning") {
    return <StatusPill variant="provisioning">Provisioning</StatusPill>;
  }
  if (integration.included) {
    return <StatusPill variant="included">Included</StatusPill>;
  }
  return null;
};

export const IntegrationCard = ({
  integration,
  featured = false,
}: IntegrationCardProps): JSX.Element => {
  const pill = renderPill(integration);

  return (
    <Link
      to={`/dashboard/integrations/${integration.slug}`}
      className={styles.card}
      aria-label={`Open ${integration.title}`}
    >
      {featured ? <span className={styles.featuredBadge}>Featured</span> : null}
      <header className={styles.header}>
        <span className={styles.logoChip}>
          <Logo
            slug={integration.slug}
            size={40}
            brandHex={integration.iconBrandHex ?? null}
            logoUrl={integration.ourLogoUrl ?? integration.logoUrl ?? null}
          />
        </span>
        <div className={styles.titleBlock}>
          <span className={styles.name}>{integration.title}</span>
          <span className={styles.category}>{integration.category}</span>
        </div>
      </header>
      <p className={styles.blurb}>{integration.description}</p>
      {pill !== null ? <div className={styles.footer}>{pill}</div> : null}
    </Link>
  );
};
